/**
 * TransformationStoryCarousel — Iter. 406.i
 *
 * Side-by-side AVANT vs APRÈS visualizer for the J42 reassessment modal.
 * Mobile-first: 3 tabs (Face / Profil / Dos), each with a horizontal
 * comparison slider — drag the divider left/right to reveal more of either
 * photo. iPhone-safe thanks to the useCameraViewport pattern inherited
 * from the parent modal.
 *
 * Data shape:
 *   pairs = {
 *     face:    { before: { url, created_at }, after: { url, created_at } } | null,
 *     profile: ...same shape...,
 *     dos:     ...same shape...
 *   }
 *
 * Behaviour:
 *   • Renders nothing when zero pairs available.
 *   • Defaults to the first available pair when the requested tab is empty.
 *   • Slider position persists across tab switches (so the user keeps their
 *     preferred "before/after weight").
 */
import React, { useMemo, useRef, useState } from 'react';
import { Camera, ImageOff } from 'lucide-react';

const POSE_LABELS = {
  face: { fr: 'Face', en: 'Front' },
  profile: { fr: 'Profil', en: 'Profile' },
  dos: { fr: 'Dos', en: 'Back' },
};

function formatDate(iso, fr) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(fr ? 'fr-CA' : 'en-CA', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  } catch {
    return (iso || '').slice(0, 10);
  }
}

function ComparisonSlider({ before, after, sliderPos, onSliderChange, backendUrl }) {
  const containerRef = useRef(null);

  const startDrag = (e) => {
    e.preventDefault();
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const move = (ev) => {
      const x = (ev.touches ? ev.touches[0].clientX : ev.clientX) - rect.left;
      const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
      onSliderChange(pct);
    };
    move(e);
    const stop = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', stop);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', stop);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', stop);
    window.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('touchend', stop);
  };

  const beforeSrc = before?.url ? (before.url.startsWith('http') ? before.url : `${backendUrl}${before.url}`) : null;
  const afterSrc = after?.url ? (after.url.startsWith('http') ? after.url : `${backendUrl}${after.url}`) : null;

  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-[3/4] rounded-xl overflow-hidden bg-black/60 border border-amber-500/40 select-none"
      style={{ touchAction: 'none' }}
      data-testid="transformation-slider"
    >
      {/* After (back layer — full width) */}
      {afterSrc ? (
        <img
          src={afterSrc}
          alt="Après"
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-white/40">
          <ImageOff className="w-10 h-10" />
        </div>
      )}

      {/* Before (front layer — clipped) */}
      {beforeSrc && (
        <div
          className="absolute inset-y-0 left-0 overflow-hidden"
          style={{ width: `${sliderPos}%` }}
        >
          <img
            src={beforeSrc}
            alt="Avant"
            className="absolute inset-y-0 left-0 h-full object-cover"
            style={{ width: containerRef.current ? `${containerRef.current.getBoundingClientRect().width}px` : '100%' }}
            draggable={false}
          />
        </div>
      )}

      {/* Divider */}
      <div
        className="absolute inset-y-0 w-[3px] bg-white shadow-[0_0_8px_rgba(255,255,255,0.7)] cursor-ew-resize"
        style={{ left: `calc(${sliderPos}% - 1.5px)` }}
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        data-testid="transformation-slider-handle"
      >
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 left-1/2 w-9 h-9 rounded-full bg-white text-black flex items-center justify-center shadow-lg ring-2 ring-amber-400">
          <span className="text-[10px] font-bold tracking-wider">⟷</span>
        </div>
      </div>

      {/* Labels */}
      <div className="absolute top-2 left-2 px-2 py-1 rounded-md bg-amber-500/90 text-black text-[10px] font-bold uppercase tracking-wider">
        Avant
      </div>
      <div className="absolute top-2 right-2 px-2 py-1 rounded-md bg-violet-500/90 text-white text-[10px] font-bold uppercase tracking-wider">
        Après
      </div>
    </div>
  );
}

export default function TransformationStoryCarousel({
  pairs = {},
  language = 'fr',
  backendUrl = '',
  testIdPrefix = 'transformation-carousel',
}) {
  const fr = language === 'fr';
  const available = useMemo(
    () => ['face', 'profile', 'dos'].filter((k) => pairs[k]?.before && pairs[k]?.after),
    [pairs]
  );
  const [activeTab, setActiveTab] = useState(available[0] || 'face');
  const [sliderPos, setSliderPos] = useState(50);

  // Whenever the available list narrows and the current tab is gone,
  // fall back to the first available pair.
  React.useEffect(() => {
    if (!available.includes(activeTab) && available[0]) {
      setActiveTab(available[0]);
    }
  }, [available, activeTab]);

  if (available.length === 0) {
    return (
      <div
        className="rounded-xl border-2 border-dashed border-amber-500/30 bg-amber-500/5 p-4 text-center"
        data-testid={`${testIdPrefix}-empty`}
      >
        <Camera className="w-7 h-7 mx-auto text-amber-400/60 mb-2" />
        <p className="text-xs text-amber-200/80">
          {fr
            ? "Aucune comparaison disponible — il faut au moins 1 photo Avant (J7) ET 1 photo Apres pour chaque pose."
            : "No comparison available yet — need at least 1 Before (Day 7) AND 1 After photo per pose."}
        </p>
      </div>
    );
  }

  const pair = pairs[activeTab];
  const beforeDate = formatDate(pair?.before?.created_at, fr);
  const afterDate = formatDate(pair?.after?.created_at, fr);

  return (
    <div className="space-y-3" data-testid={testIdPrefix}>
      {/* Tabs */}
      <div className="flex items-center justify-center gap-1.5">
        {['face', 'profile', 'dos'].map((k) => {
          const enabled = available.includes(k);
          const isActive = k === activeTab;
          return (
            <button
              key={k}
              type="button"
              disabled={!enabled}
              onClick={() => setActiveTab(k)}
              data-testid={`${testIdPrefix}-tab-${k}`}
              className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-wider font-bold transition-colors ${
                isActive
                  ? 'bg-amber-400 text-black shadow-lg'
                  : enabled
                    ? 'bg-white/10 text-white/80 hover:bg-white/20'
                    : 'bg-white/5 text-white/30 cursor-not-allowed line-through'
              }`}
            >
              {POSE_LABELS[k]?.[language] || k}
              {!enabled && ' —'}
            </button>
          );
        })}
      </div>

      {/* Slider */}
      <ComparisonSlider
        before={pair.before}
        after={pair.after}
        sliderPos={sliderPos}
        onSliderChange={setSliderPos}
        backendUrl={backendUrl}
      />

      {/* Date band */}
      <div className="flex items-center justify-between text-[10px] uppercase tracking-wider">
        <span className="text-amber-300 font-bold">
          {fr ? 'AVANT' : 'BEFORE'} · {beforeDate}
        </span>
        <span className="text-white/40 text-[9px]">
          ⟷ {fr ? 'Glisse pour comparer' : 'Slide to compare'}
        </span>
        <span className="text-violet-300 font-bold">
          {fr ? 'APRÈS' : 'AFTER'} · {afterDate}
        </span>
      </div>
    </div>
  );
}
